import { FC } from 'react'
import { Icon, Toast, useToast, useToggle } from '@revolut-internal/ui-kit'

import { getAssistantChatMessageFeedbackPayload } from 'components/ChatMessage'
import {
  AssistantChatMessageFeedback,
  AssistantChatMessageRating,
  Message,
} from 'lib/apiClient'
import { useAssistantChatMessagesFeedbackActions } from 'pages/AssistantPlayground/hooks/useAssistantChatMessageFeedbackActions'
import { ChatMessageFeedbackPopup } from 'components/ChatMessageFeedbackPopup/ChatMessageFeedbackPopup'
import { useAssistantChatMessagesContext } from 'pages/AssistantPlayground/hooks'
import { ChatFeedbackFeedbackAction } from 'components/ChatFeedbackFeedbackAction/ChatFeedbackFeedbackAction'

import { createTempAssistantChatMessageFeeback } from './ChatFeedbackActions.utils'

interface IChatFeedbackActionsProps {
  message: Message
  messageFeedback: AssistantChatMessageFeedback | null
  refetchChatMessageFeedback: (
    id: string,
    tempRating: AssistantChatMessageRating,
  ) => Promise<AssistantChatMessageFeedback | null>
}

export const ChatFeedbackActions: FC<IChatFeedbackActionsProps> = ({
  message,
  messageFeedback,
  refetchChatMessageFeedback,
}) => {
  const toast = useToast()
  const [openFeedbackPopup, toggleOpenFeedbackPopup] = useToggle()

  const { addAssistantChatMessageFeedbackById, removeAssistantChatMessageFeedbackById } =
    useAssistantChatMessagesContext()

  const { createAssistantChatMessageFeedback, isLoading } =
    useAssistantChatMessagesFeedbackActions()

  const handleCreateAssistantChatMessageFeedback = async (
    rating: AssistantChatMessageRating,
    feedback?: string,
    reasons?: string[],
  ) => {
    try {
      addAssistantChatMessageFeedbackById(
        message.messageId,
        createTempAssistantChatMessageFeeback(message.messageId, rating),
      )

      const payload = getAssistantChatMessageFeedbackPayload({
        messageId: message.messageId,
        rating,
        reasons,
        feedback,
      })
      await createAssistantChatMessageFeedback(payload)

      toast.show(
        <Toast>
          <Toast.Indicator>
            <Icon name="Check" />
          </Toast.Indicator>
          <Toast.Label>Thanks for your feedback</Toast.Label>
        </Toast>,
        'short',
      )

      await refetchChatMessageFeedback(payload.assistantGenerationId, payload.rating)
    } catch (error) {
      console.error(error)
      removeAssistantChatMessageFeedbackById(message.messageId)
    }
  }

  return (
    <>
      <ChatFeedbackFeedbackAction
        key={AssistantChatMessageRating.ThumbUp}
        value={AssistantChatMessageRating.ThumbUp}
        active={messageFeedback?.rating === AssistantChatMessageRating.ThumbUp}
        visible={
          !messageFeedback ||
          messageFeedback.rating === AssistantChatMessageRating.ThumbUp
        }
        disabled={isLoading}
        onClick={() =>
          handleCreateAssistantChatMessageFeedback(AssistantChatMessageRating.ThumbUp)
        }
      />
      <ChatFeedbackFeedbackAction
        key={AssistantChatMessageRating.ThumbDown}
        value={AssistantChatMessageRating.ThumbDown}
        active={messageFeedback?.rating === AssistantChatMessageRating.ThumbDown}
        visible={
          !messageFeedback ||
          messageFeedback.rating === AssistantChatMessageRating.ThumbDown
        }
        disabled={isLoading}
        onClick={toggleOpenFeedbackPopup.on}
      />

      <ChatMessageFeedbackPopup
        open={openFeedbackPopup}
        isLoading={isLoading}
        onClose={toggleOpenFeedbackPopup.off}
        onSubmit={(feeback, reasons) =>
          handleCreateAssistantChatMessageFeedback(
            AssistantChatMessageRating.ThumbDown,
            feeback,
            reasons,
          )
        }
      />
    </>
  )
}
